import { useState } from 'react';
import './Search.css';

interface SearchResult {
  url: string;
  title: string | null;
  metadata: {
    author: string | null;
    license: string | null;
    consent_type: string;
    attribution_url: string | null;
    crawled_at: string;
    consent_confidence: number;
  };
  score: number;
  content?: {
    snippet: string;
  };
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://goodfetch-api.workers.dev';

function Search() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/search?q=${encodeURIComponent(query)}&include=metadata,content&limit=20`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResults(data.results || []);
    } catch (err) {
      setError('Failed to search. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="search-page">
      <div className="container">
        <div className="search-header">
          <h1>Search Ethical Content</h1>
          <p className="text-secondary">
            Find content that has explicitly consented to AI training use.
          </p>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            className="input search-input"
            placeholder="Search for topics, keywords, or concepts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {error && (
          <div className="search-error card">
            <p className="text-error">{error}</p>
          </div>
        )}

        {searched && !loading && results.length === 0 && !error && (
          <div className="search-empty card">
            <p>No results found for "{query}"</p>
            <p className="text-secondary mt-2">
              Try different keywords or browse our registered domains.
            </p>
          </div>
        )}

        {results.length > 0 && (
          <div className="search-results">
            <p className="results-count text-secondary">
              Found {results.length} result{results.length !== 1 ? 's' : ''}
            </p>
            
            {results.map((result, index) => (
              <div key={index} className="result-card card">
                <div className="result-header">
                  <h3 className="result-title">
                    <a href={result.url} target="_blank" rel="noopener noreferrer">
                      {result.title || result.url}
                    </a>
                  </h3>
                  <span className="badge badge-success">
                    {result.metadata.consent_type}
                  </span>
                </div>
                
                <p className="result-url text-secondary">{result.url}</p>
                
                {result.content?.snippet && (
                  <p className="result-snippet">{result.content.snippet}</p>
                )}
                
                <div className="result-meta">
                  {result.metadata.author && (
                    <span>Author: {result.metadata.author}</span>
                  )}
                  {result.metadata.license && (
                    <span>License: {result.metadata.license}</span>
                  )}
                  <span>
                    Confidence: {Math.round(result.metadata.consent_confidence * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
